/**
 * Launching a vitamin concept test, and the one property that has to hold: a refusal must come back out.
 *
 * Extracted from `App.handleFormSavePayload`, where it was thirty lines wrapped in a `catch` that wrote the
 * error to `console.error` and returned as if the launch had succeeded. `VitaminConceptTestForm` awaits the
 * callback inside its own try/catch and renders the message under the Launch button — so the swallow made
 * that error state unreachable, and a customer with a spent quota clicked Launch and saw nothing at all.
 * The fix that added the error UI shipped and was inert; it is present in the deployed DEV bundle.
 *
 * Here rather than inline so the property is testable. Rendering the whole of `App.tsx` (1,145 lines, four
 * context providers, a router and a multi-step wizard) to assert that one callback re-throws is not a test
 * anybody would keep working, which is why the seam had none.
 */
import { createSimulation } from '../../utils/simulationApi';

/** Injected so the test drives the real function rather than a copy of its shape. */
export interface VitaminSubmitDeps {
  /** Defaults to the shipped `createSimulation`, which throws `UsageLimitExceededError` on a spent quota. */
  create?: (payload: Record<string, unknown>) => Promise<{ simulation_id: number }>;
  /**
   * Run the moment the study exists, BEFORE the notification is registered.
   *
   * Ordering preserved from the code this replaced, where the caller advanced to the results view and only
   * then awaited the email subscription. Reversing it would make every launch wait on a courtesy POST
   * before the screen changed.
   */
  onCreated?: (simulationId: number) => void;
  /** Registers the completion email. Optional, and its failure is never the caller's problem. */
  notify?: (simulationId: number) => Promise<void>;
}

/**
 * Create the run and return its id.
 *
 * Rejects with whatever `create` rejected with — the typed `UsageLimitExceededError` for a spent quota,
 * carrying the server's own sentence, or a plain `Error` otherwise. The caller decides how to show it.
 *
 * A failing `notify` is swallowed on purpose: the study exists and its id is the useful thing. Reporting a
 * courtesy notification's failure as a failed launch would strand somebody in front of a real result.
 */
export async function submitVitaminConceptTest(
  payload: Record<string, unknown>,
  { create = createSimulation, onCreated, notify }: VitaminSubmitDeps = {},
): Promise<number> {
  const { simulation_id: simulationId } = await create(payload);

  onCreated?.(simulationId);

  if (notify && simulationId) {
    try {
      await notify(simulationId);
    } catch (notifyError) {
      console.warn('Failed to set up email notification:', notifyError);
    }
  }

  return simulationId;
}
